<template>
  <div class="content">

      <div class="container-fluid mt-4">

          <stats-card title="Farm details" type="default" :sub-title="farmData.name" icon="fas fa-warehouse">

            <template slot="actions">
                <a class="dropdown-item" :href="`/#/organisations/${farmData.organisationId}`">View parent org</a>
                <a class="dropdown-item" @click="showChangeOrg = true">Change parent org</a>
                <a class="dropdown-item" v-if="farmData.status != 'deleted'" @click="showDeleteModal = true" href="javascript:void(0)">Delete Farm</a>
                <a class="dropdown-item" v-if="farmData.status != 'active'" @click="activateFarm" href="javascript:void(0)">Activate Farm</a>
            </template>

            <template slot="append">

                <base-alert type="danger" v-if="farmData.status =='deleted'">
                    This farm is currently <strong>deleted</strong>
                </base-alert>

                <b-card no-bod class="tab-wrapper">

                  <b-card-header header-tag="nav">
                      <b-nav card-header tabs>
                          <b-nav-item @click="setActiveTab(2)" :active="activeTab == 2">
                              Farm Details
                              <span v-if="healthIssues.length > 0" :class="`ml-1 badge badge-warning`">{{healthIssues.length}}</span>
                          </b-nav-item>
                          <b-nav-item @click="setActiveTab(1)" :active="activeTab == 1">
                              <i class="fas fa-tree"></i> 
                              Orchards
                          </b-nav-item>
                          <b-nav-item :disabled="true" @click="setActiveTab(3)" :active="activeTab == 3">
                              <i class="fas fa-bug"></i> 
                              Detections
                          </b-nav-item>
                      </b-nav>
                  </b-card-header>

                  <div class="tab-container">

                    <div class="mt-3" v-if="activeTab == 1">
                        <entity-picker 
                            @onSelect="handleGroupUpdate"
                            placeHolder="Search to add an orchard"
                            apiEndpoint="getGroups"
                            :multiple="true"
                            :selected="groups"
                            :canBeEmpty="true"
                            rootProp="orchards"
                            :canEdit="true"
                            editPath="/groups/"
                            :tableOptions="tableOptions"
                            :tableColumns='tableColumns'
                        >
                        </entity-picker>
                    </div>

                    <div class="mt-3" v-if="activeTab == 3">
                        <el-date-picker
                            v-model="pickedDate"
                            type="daterange"
                            format="dd/MM/yyyy"
                            range-separator="To"
                            start-placeholder="Start date"
                            end-placeholder="End date"
                            :picker-options="pickerOptions"
                            @change="onDatePick"
                            >
                        </el-date-picker>

                        <base-alert v-if="detections.length" class="mt-4">
                            Detection charts / data table will go here
                        </base-alert>
                    </div>

                    <div class="mt-3" v-if="activeTab == 2">

                      <base-alert type="warning" v-if="healthIssues.length > 0">
                          Please check this farms's missing properties:
                          <ul>
                              <li v-for="issue in healthIssues" :key="issue">{{issue}}</li>
                          </ul>
                      </base-alert>

                      <base-alert>
                        This farm was created on {{farmData.dateCreated}}
                      </base-alert>

                      <form-farm :farm="updatedFarmData"></form-farm>

                      <div v-if="!farmData.location.isValid" class="mt-1">
                            <base-alert type="info">
                                This farm has no location yet.
                            </base-alert>
                        
                            <set-location key="addressLookup" @onLocationSet="setLocation"></set-location>
                        </div>

                        <set-location v-if="farmData.location.isValid" key="locationLookup" :showInput="false" :location="farmData.location" @onLocationSet="setLocation"></set-location>

                      <div class="mt-3" align="right">
                          <base-button type="primary" @click="updateFarm">
                              Update details
                          </base-button>
                      </div>
                  </div>

                  </div>

                </b-card>

            </template>

          </stats-card>

      </div>

    <modal :show.sync="showDeleteModal">
        <h6 slot="header" class="modal-title">Delete Farm</h6>

        <base-alert type="danger">
            Are you sure you want to <strong>delete</strong> this farm? 
        </base-alert>

        <template slot="footer">
            <base-button type="blank" @click="showDeleteModal = false">Cancel</base-button>
            <base-button type="danger" @click="deleteFarm">Yes, delete</base-button>
        </template>
    </modal>

    <modal :show.sync="showChangeOrg">
        <h6 slot="header" class="modal-title">Change Parent Org</h6>

        <base-alert type="warning">
            Permissions won't be carried over for users currently associated to this farm 
        </base-alert>

        <entity-picker
            :key="orgPicker"
            :canEdit="false"
            :multiple="false"
            :canSelect="true"
            placeHolder="Select this farm's parent org"
            apiEndpoint="getOrgs"
            rootProp="organisations"
            @onSelect="onChangeOrgSelect"
        >
        </entity-picker>

        <template slot="footer">
            <base-button type="blank" @click="showChangeOrg = false; updatedFarmData.organisationId = farmData.organisationId">Cancel</base-button>
            <base-button :disabled="updatedFarmData.organisationId == farmData.organisationId" type="primary" @click="changeOrg">Change</base-button>
        </template>
    </modal>

  </div>

</template>

<script>
import api from '@/api'
import { FarmModel } from '@/models/farm/farm'
import { datePickerReadyDates } from '@/config/Time'
import { DatePicker } from 'element-ui';
import { tableColumns, tableFilters, tableOptions } from '@/config/GroupTable'
import { getEntityIssues } from '@/util/EntityHealth'
import { StringHelper } from '../helpers/StringHelper';

export default {
  components: {
      [DatePicker.name]: DatePicker
  },
  data() {
    return {
      showChangeOrg: false,
      pickedDate: '',
      justCreated: false,
      groups: [],
      originalGroups: [],
      farmData: null,
      updatedFarmData: null,
      activeTab: 2,
      deleteOrchards: true,
      showDeleteModal: false,
      detections: [],
      pickerOptions: datePickerReadyDates,
      healthItems: [
          'phone',
          'email',
          'address',
          'timezone',
          'location',
          'description'
      ],
      healthIssues: [],
      tableOptions: tableOptions,
      tableColumns: tableColumns
    };
  },
  created: async function() {
    this.fetchFarm()
  },
  methods: {
    onChangeOrgSelect(farmId) {
        this.updatedFarmData.organisationId = farmId[0].id || null
    },
    changeOrg() {
        this.updateFarm()
    },
    setLocation: function(location) {
        this.updatedFarmData.location.populate(location)
    },
    fetchFarm: async function() {
        this.$emit('loading', true);
        const results = await api.getFarm(this.$route.params.id)
        this.$emit('loading', false);
        this.justCreated = this.$route.query.created !== undefined
        this.farmData = new FarmModel(results)
        this.updatedFarmData = new FarmModel(results)
        this.checkEntityHealth();
    },
    onDatePick: async function(date) {
      this.$emit('loading', true);
      const detectionResults = await api.getSensorDetections() // TODO include daterange param & proper group detections endpoint
      this.$emit('loading', false);
      this.detections = detectionResults
    },
    toggleDeleteOrchards: function(e) {
        this.deleteOrchards = e.target.checked
    },
    setActiveTab: async function(tab) {
        if(tab == 1) {
            if(this.groups.length == 0) {
                this.$emit('loading', true);
                let groupResponse = await api.getFarmGroups(this.$route.params.id)
                this.$emit('loading', false);
                this.groups = groupResponse.orchards
                this.originalGroups = JSON.parse(JSON.stringify(groupResponse.orchards))
            }
        }
        this.activeTab = tab
    },
    handleGroupUpdate: async function(groops) {

        let originalGroupsIds = this.originalGroups.map((o) => o.id)
        let updatedGroupsIds = groops.map((g) => g.id)

        let addedGroups = groops.filter((g) => !originalGroupsIds.includes(g.id))
        let removedGroups = this.originalGroups.filter((g) => !updatedGroupsIds.includes(g.id))

        if(addedGroups.length > 0 || removedGroups.length > 0) {
            this.$emit('loading', true);

            if(addedGroups.length > 0) {
                for(let i in addedGroups) {
                    let farmId = this.farmData.id
                    let groupUpdate = await api.updateGroup(addedGroups[i].id,{
                        farmId: farmId 
                    })
                }
                this.$emit('onApiResult', `${addedGroups.length} orchard(s) added to farm`);
            }

            if(removedGroups.length > 0) {
                for(let i in removedGroups) {
                    let groupUpdate = await api.updateGroup(removedGroups[i].id,{
                        status: 'deleted' 
                    })
                }
                this.$emit('onApiResult', `${removedGroups.length} orchard(s) removed from farm`);
            }
            this.originalGroups = []
            this.groups = []
            this.setActiveTab(1)
        }
    },
    updateFarm: async function() {
      this.updatedFarmData = StringHelper.trimObjectStrings(this.updatedFarmData);
      this.$emit('loading', true);
      const results = await api.updateFarm(this.farmData.id,this.updatedFarmData.toDetailsUpdateJson(this.farmData))
      this.fetchFarm()
      this.$emit('onApiResult', "Farm has been updated");
    },
    deleteFarm: async function() {
        this.showDeleteModal = false
        this.updatedFarmData.status = 'deleted'
        this.updateFarm()
    },
    activateFarm: async function() {
        this.updatedFarmData.status = 'active'
        this.updateFarm()
    },
    checkEntityHealth: function() {
        this.healthIssues = getEntityIssues(this.healthItems,this.farmData)
    }
    
  }
};
</script>
